import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Facebook, Twitter, Youtube, Heart, MapPin, Mail, Phone } from "lucide-react";
import { teamInfo, instagramFeed } from "../../data/arsenalData";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const FACEBOOK_URL = "https://www.facebook.com/arsenaldeallende";

export const Footer = () => {
  const [lightboxPost, setLightboxPost] = useState(null);

  return (
    <footer
      data-testid="main-footer"
      className="relative bg-[#061930] text-white overflow-hidden border-t border-white/10"
    >
      <Dialog open={lightboxPost != null} onOpenChange={(open) => !open && setLightboxPost(null)}>
        <DialogContent className="max-h-[95vh] max-w-[min(96vw,1200px)] overflow-y-auto border border-[#DB0007]/30 bg-[#061930] p-3 text-white sm:p-6">
          <DialogTitle className="sr-only">{lightboxPost?.caption ?? "Publicación"}</DialogTitle>
          {lightboxPost && (
            <>
              <img
                src={lightboxPost.image}
                alt={lightboxPost.caption}
                className="mx-auto max-h-[min(78vh,900px)] w-full max-w-full object-contain"
              />
              <p className="font-outfit mt-3 text-center text-sm text-white/90">{lightboxPost.caption}</p>
              <p className="font-mono-jb text-center text-[10px] uppercase tracking-widest text-[#9C824A]">
                {lightboxPost.likes} me gusta
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Instagram feed section */}
      <section className="py-20 md:py-24 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-end justify-between flex-wrap gap-4 mb-10"
          >
            <div>
              <div className="section-overline">Social Media</div>
              <h2 className="font-anton uppercase text-2xl sm:text-3xl lg:text-4xl mt-4 tracking-tight leading-snug">
                Síguenos en{" "}
                <a
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#DB0007] hover:text-[#9C824A] transition-colors underline-offset-4 hover:underline font-outfit font-semibold normal-case tracking-normal text-lg sm:text-xl lg:text-2xl"
                  data-testid="footer-social-headline-link"
                >
                  @arsenaldeallende
                </a>
              </h2>
            </div>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-btn"
              className="btn-outline-gold shrink-0"
            >
              <Facebook size={18} />
              <span>Facebook</span>
            </a>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
            {instagramFeed.map((post, i) => (
              <motion.button
                key={post.id}
                type="button"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="group relative aspect-square overflow-hidden bg-[#0a2240] cursor-pointer border-0 p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DB0007]"
                data-testid={`instagram-post-${post.id}`}
                onClick={() => setLightboxPost(post)}
              >
                <img
                  src={post.image}
                  alt={post.caption}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-[#DB0007]/0 group-hover:bg-[#DB0007]/40 backdrop-blur-0 group-hover:backdrop-blur-sm transition-all flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 p-3 text-center">
                  <Heart size={20} className="text-white fill-white mb-2" />
                  <span className="font-anton text-xl">{post.likes}</span>
                  <span className="font-outfit text-xs text-white/80 mt-1 line-clamp-2">
                    {post.caption}
                  </span>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Main footer */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid md:grid-cols-12 gap-10">
            {/* Brand */}
            <div className="md:col-span-4">
              <div className="flex items-center gap-3">
                <img src={teamInfo.logo} alt="Arsenal" className="w-16 h-16 object-contain" />
                <div>
                  <div className="font-anton uppercase text-2xl leading-none tracking-tight">
                    Arsenal
                  </div>
                  <div className="font-outfit text-xs uppercase tracking-[0.3em] text-[#9C824A]">
                    de Allende · N.L.
                  </div>
                </div>
              </div>
              <p className="font-outfit text-white/60 text-sm mt-5 leading-relaxed max-w-sm">
                {teamInfo.slogan} Fundado en {teamInfo.founded}, somos el orgullo de Allende, Nuevo León.
              </p>

              {/* Social icons */}
              <div className="flex gap-2 mt-6">
                {[
                  { Icon: Instagram, label: "instagram", href: "#" },
                  { Icon: Facebook, label: "facebook", href: FACEBOOK_URL },
                  { Icon: Twitter, label: "twitter", href: "#" },
                  { Icon: Youtube, label: "youtube", href: "#" },
                ].map(({ Icon, label, href }) => (
                  <a
                    key={label}
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                    data-testid={`social-${label}`}
                    className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10 hover:bg-[#DB0007] hover:border-[#DB0007] transition-all"
                  >
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            </div>

            {/* Links */}
            <FooterColumn
              title="Club"
              links={["Historia", "Plantilla", "Staff", "Directiva"]}
            />
            <FooterColumn
              title="Partidos"
              links={["Calendario", "Resultados", "Tabla", "Torneos"]}
            />

            {/* Contact */}
            <div className="md:col-span-3">
              <h4 className="font-anton uppercase text-sm tracking-widest text-[#9C824A] mb-5">
                Contacto
              </h4>
              <ul className="space-y-3 font-outfit text-sm text-white/70">
                <li className="flex items-start gap-2">
                  <MapPin size={14} className="text-[#DB0007] mt-0.5 shrink-0" />
                  <span>Canchas de las Colonias del Norte, Allende, Nuevo León</span>
                </li>
                <li className="flex items-center gap-2">
                  <Mail size={14} className="text-[#DB0007] shrink-0" />
                  <span>contacto@arsenaldeallende.com</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone size={14} className="text-[#DB0007] shrink-0" />
                  <span>+52 826 166 6837</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-14 pt-6 border-t border-white/10 flex items-center justify-between flex-wrap gap-4">
            <p className="font-mono-jb text-xs text-white/40 uppercase tracking-widest">
              © 2026 Arsenal de Allende · Fundado 2012 · Allende, N.L.
            </p>
            <p className="font-outfit text-xs text-white/40">
              Hecho con <Heart size={12} className="inline text-[#DB0007] fill-[#DB0007]" /> para la afición arsenalista
            </p>
          </div>
        </div>
      </section>
    </footer>
  );
};

const FooterColumn = ({ title, links }) => (
  <div className="md:col-span-2">
    <h4 className="font-anton uppercase text-sm tracking-widest text-[#9C824A] mb-5">{title}</h4>
    <ul className="space-y-3 font-outfit text-sm">
      {links.map((link) => (
        <li key={link}>
          <a
            href="#"
            data-testid={`footer-link-${link.toLowerCase()}`}
            className="text-white/70 hover:text-[#DB0007] transition-colors"
          >
            {link}
          </a>
        </li>
      ))}
    </ul>
  </div>
);
